import { AccountStatusKind } from "@repo/app-types/entities/company";
import type {
  AdminApiAiUsage,
  AdminApiAiUsageByFeature,
  AdminApiAiUsageByItem,
} from "@repo/app-types/entities/creditUsage";
import { resolvePeriodCreditCapacity } from "@repo/app-types/entities/metronome";
import {
  getAiFeatureDefinition,
  USAGE_BASED_FEATURE_UNIT_NOUNS,
  type UsageBasedFeatureKey,
} from "@repo/app-types/helpers/aiFeatureCatalog";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconChevronRight } from "@tabler/icons-react";
import { Fragment, useState, type FC } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useAiUsageQuery } from "../../../api/entities/company.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  CreditUsageCard,
  CreditUsageCardSkeleton,
  formatPeriodProjection,
  useCreditUsageSection,
} from "./creditUsageSection.js";
import {
  formatCreditsUsed,
  formatOverage,
  formatPeriodLabel,
  formatPeriodResetDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface AiCreditsSectionProps {
  periodOffset: number;
}

/**
 * The AI usage section of the billing page: usage bar against Metronome's
 * current-period credit budget and the per-feature breakdown, for the period
 * the page-level header selects.
 * Usage-only preview — nothing here bills, alerts, or pauses features.
 */
export const AiCreditsSection: FC<AiCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const company = useAppSelector(selectCompany);
  const { selectedUsage, isUsageLoading } = useCreditUsageSection(
    useAiUsageQuery,
    periodOffset,
  );
  // A pilot's credits are counted, never paced against a grant; an opt-out's are.
  const isPilot =
    company?.settings.account_status.kind === AccountStatusKind.Pilot;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">AI credits</h2>
      {selectedUsage && !isUsageLoading ? (
        isPilot ? (
          <AiCreditsIncludedCard usage={selectedUsage} />
        ) : (
          <AiCreditsUsageOverview usage={selectedUsage} />
        )
      ) : (
        <CreditUsageCardSkeleton />
      )}
      <UsageBreakdown
        features={selectedUsage?.features ?? []}
        isLoading={isUsageLoading}
      />
    </section>
  );
};

interface AiCreditsUsageOverviewProps {
  usage: AdminApiAiUsage;
}

/** The purchased packs covering the period: what they granted and what is left. */
interface PurchasedCredits {
  granted: number;
  remaining: number;
}

function resolvePurchasedCredits(
  usage: AdminApiAiUsage,
): PurchasedCredits | null {
  const { balances } = usage;
  return balances.kind !== "unknown" && balances.granted_purchased_credits > 0
    ? {
        granted: balances.granted_purchased_credits,
        remaining: balances.remaining_purchased_credits,
      }
    : null;
}

/** The selected period's closing detail: when it resets, or which period it was. */
function describePeriod(usage: AdminApiAiUsage): string {
  return usage.selected_period.offset === 0
    ? `Resets ${formatPeriodResetDate(usage.selected_period)}`
    : formatPeriodLabel(usage.selected_period);
}

/**
 * The AI credits card for the selected period. A company with a monthly grant
 * sees usage paced against it; purchased packs are named in a row of their
 * own while in reserve, and join the bar as a second segment once the grant
 * is spent and they start paying. Without a grant, the card falls back to
 * pacing every credit the period can still spend.
 */
export const AiCreditsUsageOverview: FC<AiCreditsUsageOverviewProps> = (
  props,
) => {
  const { usage } = props;
  const included = usage.included_ai_credits;
  const purchased = resolvePurchasedCredits(usage);

  if (included === null || included === 0) {
    return <UsageAgainstCapacityCard usage={usage} />;
  }

  const monthlyUsed = usage.coverage.from_included_credits;
  const purchasedUsed = usage.coverage.from_purchased_credits;
  const secondaryBalance = purchased
    ? {
        label: "Added credits",
        value: `${formatWholeNumber(purchased.remaining)} of ${formatWholeNumber(purchased.granted)} remaining`,
      }
    : undefined;

  if (purchased && purchasedUsed > 0) {
    const capacity = included + purchased.granted;
    return (
      <CreditUsageCard
        title="Monthly AI credit usage"
        headline={`${formatWholeNumber(monthlyUsed + purchasedUsed)} of ${formatWholeNumber(capacity)} used`}
        bar={
          <CombinedUsageBar
            included={included}
            monthlyUsed={monthlyUsed}
            purchasedUsed={purchasedUsed}
            capacity={capacity}
          />
        }
        details={[
          `Monthly limit: ${formatWholeNumber(included)}`,
          formatOverage(usage.credits_overage),
          describePeriod(usage),
        ]}
        projection={formatPeriodProjection(usage)}
        secondaryBalance={secondaryBalance}
      />
    );
  }

  const isCurrentPeriod = usage.selected_period.offset === 0;
  return (
    <CreditUsageCard
      title={isCurrentPeriod ? "Monthly AI credits" : "AI credits used"}
      headline={
        isCurrentPeriod
          ? `${formatWholeNumber(Math.max(0, included - monthlyUsed))} left`
          : formatWholeNumber(usage.credits_used)
      }
      bar={{
        percentage: (monthlyUsed / included) * 100,
        isExhausted: monthlyUsed >= included,
      }}
      details={[
        formatUsageAgainstIncluded(
          monthlyUsed,
          included,
          "included credits used",
        ),
        formatOverage(usage.credits_overage),
        describePeriod(usage),
      ]}
      projection={formatPeriodProjection(usage)}
      secondaryBalance={secondaryBalance}
    />
  );
};

/** A pilot's card: what the period consumed, with no limit to pace it against. */
export const AiCreditsIncludedCard: FC<AiCreditsUsageOverviewProps> = (
  props,
) => {
  const { usage } = props;
  return (
    <CreditUsageCard
      title="AI credits used"
      headline={formatWholeNumber(usage.credits_used)}
      details={[formatPeriodLabel(usage.selected_period)]}
      projection={formatPeriodProjection(usage)}
    />
  );
};

/**
 * A period without a monthly grant. A live one paces every credit it can
 * still spend; a closed one reports what was consumed.
 */
const UsageAgainstCapacityCard: FC<AiCreditsUsageOverviewProps> = (props) => {
  const { usage } = props;
  const capacity = resolvePeriodCreditCapacity(usage);
  const hasCapacity = capacity !== null && capacity > 0;

  return (
    <CreditUsageCard
      title={
        usage.balances.kind === "live"
          ? "Monthly AI credits"
          : "AI credits used"
      }
      headline={
        usage.balances.kind === "live"
          ? `${formatWholeNumber(usage.balances.remaining_credits)} left`
          : formatWholeNumber(usage.credits_used)
      }
      bar={
        hasCapacity
          ? {
              percentage: (usage.credits_used / capacity) * 100,
              isExhausted: usage.credits_overage > 0,
            }
          : undefined
      }
      details={[
        hasCapacity
          ? formatUsageAgainstIncluded(
              usage.credits_used,
              capacity,
              "credits used",
            )
          : `${formatWholeNumber(usage.credits_used)} credits used`,
        formatOverage(usage.credits_overage),
        describePeriod(usage),
      ]}
      projection={formatPeriodProjection(usage)}
    />
  );
};

interface CombinedUsageBarProps {
  included: number;
  monthlyUsed: number;
  purchasedUsed: number;
  capacity: number;
}

/**
 * Monthly and purchased credits on one bar: the grant's segment, a tick at
 * the monthly limit, then what the packs paid for past it, with a key to the
 * two segments beneath.
 */
const CombinedUsageBar: FC<CombinedUsageBarProps> = (props) => {
  const { included, monthlyUsed, purchasedUsed, capacity } = props;
  const toPercent = (credits: number) =>
    Math.min(100, (credits / capacity) * 100);
  const monthlyLimitPercent = toPercent(included);
  const totalUsedPercent = toPercent(monthlyUsed + purchasedUsed);

  return (
    <div>
      <div
        aria-label={`${Math.round(totalUsedPercent)}% of available credits used`}
        className="relative mt-3 h-2"
        role="meter"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={Math.round(totalUsedPercent)}
      >
        <div className="absolute inset-0 overflow-hidden rounded-full bg-gray-100">
          <div
            className="absolute inset-y-0 left-0 bg-indigo-600"
            style={{ width: `${toPercent(monthlyUsed)}%` }}
          />
          <div
            className="absolute inset-y-0 bg-teal-500"
            style={{
              left: `${monthlyLimitPercent}%`,
              width: `${toPercent(purchasedUsed)}%`,
            }}
          />
        </div>
        <div
          aria-hidden="true"
          className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-950 ring-1 ring-white"
          style={{ left: `${monthlyLimitPercent}%` }}
        />
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-indigo-600"
          />
          Monthly
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-teal-500"
          />
          Added
        </span>
      </div>
    </div>
  );
};

interface UsageBreakdownProps {
  features: readonly AdminApiAiUsageByFeature[];
  isLoading: boolean;
}

const UsageBreakdown: FC<UsageBreakdownProps> = (props) => {
  const { features: rows, isLoading } = props;

  return (
    <SettingsTable>
      <caption className="sr-only">Usage by feature</caption>
      <SettingsTableHeaderSection>
        <tr>
          <SettingsTableHeaderCell className="w-[45%]">
            Feature
          </SettingsTableHeaderCell>
          <SettingsTableHeaderCell className="w-[30%]">
            Volume
          </SettingsTableHeaderCell>
          <SettingsTableHeaderCell className="w-[25%] text-right">
            Credits used
          </SettingsTableHeaderCell>
        </tr>
      </SettingsTableHeaderSection>
      <SettingsTableBodySection>
        {isLoading ? <TableSkeleton /> : null}
        {rows.map((row) => (
          <FeatureUsageRow key={row.feature_key} row={row} />
        ))}
      </SettingsTableBodySection>
    </SettingsTable>
  );
};

interface FeatureUsageRowProps {
  row: AdminApiAiUsageByFeature;
}

/** Cells carry the row's background, so the row's hover shade lands on each. */
const FEATURE_ROW_CELL_CLASS_NAME = "group-hover/row:bg-gray-100";

/** A feature's row, expandable into its items when it has any. */
const FeatureUsageRow: FC<FeatureUsageRowProps> = (props) => {
  const { row } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const featureLabel =
    getAiFeatureDefinition(row.feature_key)?.label ?? row.feature_key;
  const includedItemCount = row.items.filter((item) => item.is_free).length;
  const label = (
    <span>
      {featureLabel}
      {includedItemCount > 0 ? (
        <span className="text-muted-foreground">
          {` (${formatWholeNumber(includedItemCount)} included)`}
        </span>
      ) : null}
    </span>
  );

  return (
    <Fragment>
      <tr className="group/row relative">
        <SettingsTableDataCell className={FEATURE_ROW_CELL_CLASS_NAME}>
          {row.items.length > 0 ? (
            // The ::after overlay stretches the button's click area across the row.
            <AriaButton
              aria-expanded={isExpanded}
              onPress={() => setIsExpanded(!isExpanded)}
              className="flex w-full items-center gap-2 text-left after:absolute after:inset-0"
            >
              <IconChevronRight
                aria-hidden="true"
                className={twMerge(
                  "size-4 shrink-0 text-muted-foreground transition-transform duration-150",
                  isExpanded && "rotate-90",
                )}
              />
              {label}
            </AriaButton>
          ) : (
            <span className="block pl-6">{label}</span>
          )}
        </SettingsTableDataCell>
        <SettingsTableDataCell className={FEATURE_ROW_CELL_CLASS_NAME}>
          {formatFeatureUnitCount(row.feature_key, row.feature_unit_count)}
        </SettingsTableDataCell>
        <SettingsTableDataCell
          className={twMerge(
            FEATURE_ROW_CELL_CLASS_NAME,
            "text-right tabular-nums",
          )}
        >
          {formatCreditsUsed(row.credits_used)}
        </SettingsTableDataCell>
      </tr>
      {isExpanded
        ? row.items.map((item) => (
            <tr key={item.item_id ?? ""}>
              <SettingsTableDataCell className="bg-gray-50 text-muted-foreground">
                <span className="block pl-10">{describeItem(item)}</span>
              </SettingsTableDataCell>
              <SettingsTableDataCell className="bg-gray-50 text-muted-foreground">
                {formatFeatureUnitCount(
                  row.feature_key,
                  item.feature_unit_count,
                )}
              </SettingsTableDataCell>
              <SettingsTableDataCell className="bg-gray-50 text-right tabular-nums">
                {formatCreditsUsed(item.credits_used)}
                {item.is_free ? (
                  <span className="text-muted-foreground"> (included)</span>
                ) : null}
              </SettingsTableDataCell>
            </tr>
          ))
        : null}
    </Fragment>
  );
};

function describeItem(item: AdminApiAiUsageByItem): string {
  if (item.item_id === null) {
    return "Other";
  }
  return item.item_name || "Unnamed";
}

function formatFeatureUnitCount(
  featureKey: UsageBasedFeatureKey,
  featureUnitCount: number,
): string {
  const featureUnitNoun = USAGE_BASED_FEATURE_UNIT_NOUNS[featureKey];
  const noun =
    featureUnitCount === 1 ? featureUnitNoun.singular : featureUnitNoun.plural;
  return `${formatWholeNumber(featureUnitCount)} ${noun}`;
}
